import { useState, useEffect, useCallback } from 'react';
import { Avatar, Button, Input, Select, Spin, Tooltip } from 'antd';
import {
  PhoneOutlined, BellFilled, CheckSquareOutlined, CalendarOutlined,
  ReloadOutlined, DownOutlined, CheckCircleFilled, ThunderboltOutlined,
  CloseOutlined, UserOutlined,
} from '@ant-design/icons';
import ActivityItem, { TYPE_CONFIG } from './ActivityItem';
import ActiviteService from '../../services/ActiviteService';
import UserService from '../../services/UserService';
import { catchError } from '../../services/DaoService';
import Toast from '../../helpers/Toast';
import { useUserContext } from '../../contexts/UserContext';
import styles from './ActiviteFeed.module.scss';
import PropTypes from 'prop-types';

/* ── Type config ───────────────────────── */
const QUICK_TYPES = [
  {
    key: 'Appel',
    label: 'Appel',
    icon: <PhoneOutlined />,
    color: '#FA8C16',
    description: 'Programmer un appel téléphonique',
    endLabel: "Date & heure de l'appel",
    hasStart: false,
  },
  {
    key: 'Tache',
    label: 'Tâche',
    icon: <CheckSquareOutlined />,
    color: 'var(--primary-color, #3E65AC)',
    description: 'Assigner une tâche spontanée',
    endLabel: "Date d'échéance",
    hasStart: false,
  },
  {
    key: 'Rappel',
    label: 'Rappel',
    icon: <BellFilled />,
    color: '#722ED1',
    description: 'Créer un rappel pour ne rien oublier',
    endLabel: 'Me rappeler le',
    hasStart: false,
  },
  {
    key: 'Reunion',
    label: 'Réunion',
    icon: <CalendarOutlined />,
    color: '#52C41A',
    description: 'Planifier une réunion',
    endLabel: 'Fin de réunion',
    hasStart: true,
    startLabel: 'Début',
  },
];

/* ── Inline quick-add form ─────────────── */
function QuickAddForm({ type, users, parentId, target, onCreated, onCancel, currentUser }) {
  const cfg = QUICK_TYPES.find(t => t.key === type);
  const typeCfg = TYPE_CONFIG[type] ?? TYPE_CONFIG.Rappel;
  const [nom, setNom] = useState('');
  const [endAt, setEndAt] = useState('');
  const [startAt, setStartAt] = useState('');
  const [whoid, setWhoid] = useState(currentUser?.id ?? null);
  const [saving, setSaving] = useState(false);

  const save = () => {
    if (!nom.trim() || !endAt) return;
    setSaving(true);
    ActiviteService.getInstance()
      .create({ type, nom: nom.trim(), end_at: endAt, start_at: startAt || null, whoid, parent_id: parentId, target })
      .then(res => { onCreated(res.data); Toast.success(res.message); })
      .catch(catchError)
      .finally(() => setSaving(false));
  };

  const PLACEHOLDERS = {
    Appel:   'Ex : Appel de suivi client Dupont',
    Tache:   'Ex : Préparer le rapport mensuel',
    Rappel:  'Ex : Rappel signature du contrat',
    Reunion: 'Ex : Réunion de lancement projet',
  };

  return (
    <div className={styles.inlineForm}>
      <div className={styles.formTypeHeader}>
        <div className={styles.formTypeIcon} style={{ background: typeCfg.color }}>
          {typeCfg.icon}
        </div>
        <div>
          <div className={styles.formTypeLabel}>{cfg?.label}</div>
          <p className={styles.formTypeDesc}>{cfg?.description}</p>
        </div>
      </div>

      <div className={styles.formGrid}>
        <div className={`${styles.formField} ${styles.formFullRow}`}>
          <label>Titre *</label>
          <Input
            autoFocus
            value={nom}
            onChange={e => setNom(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') save(); }}
            placeholder={PLACEHOLDERS[type] ?? ''}
          />
        </div>

        {cfg?.hasStart && (
          <div className={styles.formField}>
            <label>{cfg.startLabel}</label>
            <Input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} />
          </div>
        )}

        <div className={styles.formField}>
          <label>{cfg?.endLabel ?? 'Échéance'} *</label>
          <Input type="datetime-local" value={endAt} onChange={e => setEndAt(e.target.value)} />
        </div>

        <div className={styles.formField}>
          <label>Assigné à</label>
          <Select
            value={whoid}
            onChange={setWhoid}
            showSearch
            optionFilterProp="children"
            allowClear
            placeholder="Moi par défaut"
          >
            {users.map(u => (
              <Select.Option key={u.id} value={u.id}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Avatar size={16} src={u.avatar} icon={<UserOutlined />} />
                  {u.name}
                </span>
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>

      <div className={styles.formActions}>
        <Button size="small" icon={<CloseOutlined />} onClick={onCancel}>Annuler</Button>
        <Button
          size="small"
          type="primary"
          loading={saving}
          disabled={!nom.trim() || !endAt}
          style={{ background: typeCfg.color, borderColor: typeCfg.color }}
          onClick={save}
        >
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

/* ── Main ActivityFeed ─────────────────── */
export default function ActivityFeed({ parentId, parentType }) {
  const { currentUser } = useUserContext();
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  useEffect(() => {
    UserService.getInstance().all()
      .then(r => setUsers(r.data))
      .catch(catchError);
  }, []);

  const load = useCallback((append = false, url = null) => {
    if (append) setLoadingMore(true); else setLoading(true);
    const req = url
      ? ActiviteService.post(url, { parent_type: parentType })
      : ActiviteService.getInstance().findByParent(parentId, parentType);

    req.then(res => {
      const data = res.data;
      setMeta(data);
      setActivities(prev => {
        const incoming = data.data ?? [];
        if (!append) return incoming;
        const ids = new Set(prev.map(a => a.id));
        return [...prev, ...incoming.filter(a => !ids.has(a.id))];
      });
    }).catch(catchError).finally(() => { setLoading(false); setLoadingMore(false); });
  }, [parentId, parentType]);

  useEffect(() => { load(); }, [load]);

  /* Split pending / done */
  const pending = activities.filter(a => !a.done);
  const done    = activities.filter(a => a.done);

  const handleToggleDone = (updated) => {
    setActivities(prev => prev.map(a => a.id === updated.id ? updated : a));
  };
  const handleDelete = (activity) => {
    setActivities(prev => prev.filter(a => a.id !== activity.id));
  };
  const handleUpdate = (updated) => {
    setActivities(prev => prev.map(a => a.id === updated.id ? updated : a));
  };
  const handleCreated = (activity) => {
    setActivities(prev => [activity, ...prev]);
    setSelectedType(null);
  };

  return (
    <div className={styles.feed}>

      {/* ── Header ─────────────────────── */}
      <div className={styles.feedHeader}>
        <h3 className={styles.feedTitle}>
          <ThunderboltOutlined className={styles.titleIcon} />
          Activités &amp; Rappels
          {pending.length > 0 && (
            <span className={styles.feedCount}>{pending.length} en cours</span>
          )}
        </h3>
        <Tooltip title="Rafraîchir">
          <Button type="text" size="small" icon={<ReloadOutlined />} loading={loading} onClick={() => load()} />
        </Tooltip>
      </div>

      {/* ── Quick-add type buttons ──────── */}
      <div className={styles.quickAddBar}>
        <div className={styles.quickAddLabel}>Ajouter une activité</div>
        <div className={styles.quickAddTypes}>
          {QUICK_TYPES.map(t => {
            const isActive = selectedType === t.key;
            return (
              <button
                key={t.key}
                className={`${styles.typeBtn} ${isActive ? styles.active : ''}`}
                style={{
                  borderColor: t.color,
                  color: isActive ? '#fff' : t.color,
                  background: isActive ? t.color : 'transparent',
                }}
                onClick={() => setSelectedType(isActive ? null : t.key)}
              >
                <span className={styles.typeBtnIcon}>{t.icon}</span>
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Inline form ────────────────── */}
      <div className={`${styles.inlineFormWrap} ${selectedType ? styles.open : ''}`}>
        {selectedType && (
          <QuickAddForm
            type={selectedType}
            users={users}
            parentId={parentId}
            target={parentType}
            currentUser={currentUser}
            onCreated={handleCreated}
            onCancel={() => setSelectedType(null)}
          />
        )}
      </div>

      {/* ── Timeline body ──────────────── */}
      <Spin spinning={loading}>
        <div className={styles.timelineBody}>

          {/* À FAIRE */}
          <div className={styles.sectionHeader}>
            <ThunderboltOutlined style={{ color: 'var(--primary-color, #3E65AC)', fontSize: 12 }} />
            <span className={styles.sectionTitle}>À faire</span>
            <span className={styles.sectionBadge}>{pending.length}</span>
          </div>
          <div className={styles.sectionDivider} />

          {pending.length === 0 && !loading ? (
            <div className={styles.emptyState}>
              <CheckCircleFilled className={styles.emptyIcon} />
              <span>Aucune activité en cours — tout est à jour !</span>
            </div>
          ) : (
            <div className={styles.timeline}>
              {pending.map(a => (
                <ActivityItem
                  key={a.id}
                  item={a}
                  users={users}
                  onDelete={handleDelete}
                  onToggleDone={handleToggleDone}
                  onUpdate={handleUpdate}
                />
              ))}
            </div>
          )}

          {/* TERMINÉES */}
          <div
            className={styles.sectionHeader}
            style={{ marginTop: 8, cursor: 'pointer' }}
            onClick={() => setShowDone(s => !s)}
          >
            <CheckCircleFilled style={{ color: '#52C41A', fontSize: 12 }} />
            <span className={styles.sectionTitle}>Terminées</span>
            <span className={styles.sectionBadge}>{done.length}</span>
            <DownOutlined
              className={`${styles.sectionChevron} ${showDone ? styles.rotated : ''}`}
            />
          </div>
          <div className={styles.sectionDivider} />

          {showDone && (
            done.length === 0 ? (
              <div className={styles.emptyState}>
                <span>Aucune activité terminée</span>
              </div>
            ) : (
              <div className={styles.timeline}>
                {done.map(a => (
                  <ActivityItem
                    key={a.id}
                    item={a}
                    users={users}
                    onDelete={handleDelete}
                    onToggleDone={handleToggleDone}
                    onUpdate={handleUpdate}
                  />
                ))}
              </div>
            )
          )}

          {/* Load more */}
          {meta?.next_page_url && (
            <div className={styles.loadMoreRow}>
              <Button
                size="small"
                type="text"
                loading={loadingMore}
                onClick={() => load(true, meta.next_page_url)}
              >
                Charger plus d'activités
              </Button>
            </div>
          )}
        </div>
      </Spin>
    </div>
  );
}

ActivityFeed.propTypes = {
  parentId:   PropTypes.number.isRequired,
  parentType: PropTypes.string.isRequired,
};
